import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";
import { FaTimes, FaWhatsapp } from "react-icons/fa";
import { apihubPost } from "../api/apihub";
import { businessPath } from "../config/businessMenu";

const META_APP_ID = process.env.REACT_APP_META_APP_ID;
const META_CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID;
const META_GRAPH_VERSION = process.env.REACT_APP_META_GRAPH_VERSION || "v25.0";
const FB_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const FB_SDK_SCRIPT_ID = "wa-interface-fb-sdk";
const SIGNUP_TIMEOUT_MS = 300000;

const FB_MESSAGE_ORIGINS = [
  "https://www.facebook.com",
  "https://web.facebook.com",
];

const SIGNUP_FINISH_EVENTS = [
  "FINISH",
  "FINISH_ONLY_WABA",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
];

const CONNECT_STEPS = {
  IDLE: 0,
  SIGNUP_POPUP: 2,
  SAVING: 3,
  SAVED: 4,
  FAILED: 5,
};

function logConnect(label, detail) {
  if (detail === undefined) {
    console.log(`[wa-connect] ${label}`);
    return;
  }
  console.log(`[wa-connect] ${label}`, detail);
}

function loadFacebookSdk() {
  const win = window;
  if (!win) return Promise.reject(new Error("Window is not available"));
  if (win.__fbSdkPromise) return win.__fbSdkPromise;

  win.__fbSdkPromise = new Promise((resolve, reject) => {
    if (win.FB) {
      resolve(win.FB);
      return;
    }

    win.fbAsyncInit = function fbAsyncInit() {
      try {
        win.FB.init({
          appId: META_APP_ID,
          autoLogAppEvents: true,
          xfbml: true,
          version: META_GRAPH_VERSION,
        });
        resolve(win.FB);
      } catch (error) {
        reject(error);
      }
    };

    if (document.getElementById(FB_SDK_SCRIPT_ID)) {
      resolve(win.FB);
      return;
    }

    const script = document.createElement("script");
    script.id = FB_SDK_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = FB_SDK_SRC;
    script.onload = () => {
      if (win.FB) resolve(win.FB);
    };
    script.onerror = () => reject(new Error("Failed loading Facebook SDK"));
    document.body.appendChild(script);
  });

  return win.__fbSdkPromise;
}

function parseSignupMessage(event) {
  if (!FB_MESSAGE_ORIGINS.includes(event.origin)) return null;

  let data = event.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (!data || data.type !== "WA_EMBEDDED_SIGNUP") return null;
  return data;
}

function normalizeProjectId(value) {
  const id = String(value || "").trim();
  return id || null;
}

function waitForEmbeddedSignupFinish({ projectId = null, getCode, onStep } = {}) {
  return new Promise((resolve, reject) => {
    let done = false;

    const finish = (payload) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      resolve(payload);
    };

    const fail = (error) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      reject(error);
    };

    const onMessage = async (event) => {
      const data = parseSignupMessage(event);
      if (!data) return;

      const eventName = String(data.event || data.event_name || "").toUpperCase();
      logConnect("signup event", {
        origin: event.origin,
        event: eventName,
        data: data.data || null,
        raw: data,
      });

      if (eventName === "CANCEL" || eventName === "ERROR") {
        fail(new Error(
          eventName === "CANCEL"
            ? "WhatsApp signup was cancelled."
            : data?.data?.error_message || "WhatsApp signup failed.",
        ));
        return;
      }

      if (!SIGNUP_FINISH_EVENTS.includes(eventName)) return;

      const wabaId = data?.data?.waba_id || null;
      const phoneNumberId = data?.data?.phone_number_id || null;
      if (!wabaId) {
        logConnect("finish event without waba_id", { event: eventName, data: data.data });
        return;
      }

      try {
        onStep?.(CONNECT_STEPS.SAVING);
        const payload = {
          projectId: projectId || null,
          wabaId,
          phoneNumberId,
          code: getCode?.() || null,
        };
        logConnect("posting connectwhatsapp", payload);
        const saved = await apihubPost("/getInteractedCustomerDetail/connectwhatsapp", payload);
        logConnect("connectwhatsapp saved", {
          wabaId,
          phoneNumberId,
          projectId: saved?.data?.projectId || projectId || null,
        });
        onStep?.(CONNECT_STEPS.SAVED);
        finish({
          waba_id: wabaId,
          phone_number_id: phoneNumberId,
          projectId: saved?.data?.projectId || projectId || null,
        });
      } catch (error) {
        logConnect("connectwhatsapp failed", error?.message || error);
        fail(error);
      }
    };

    window.addEventListener("message", onMessage);
    setTimeout(() => {
      logConnect("signup timed out", { timeoutMs: SIGNUP_TIMEOUT_MS });
      fail(new Error("WhatsApp signup timed out. Please try again."));
    }, SIGNUP_TIMEOUT_MS);
  });
}

function requestFacebookLoginCode({ configId, extras }) {
  logConnect("FB.login started", { configId, extras });
  return new Promise((resolve) => {
    window.FB.login(
      (response) => {
        logConnect("FB.login response", {
          status: response?.status || null,
          hasCode: Boolean(response?.authResponse?.code),
        });
        resolve(response);
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras,
      },
    );
  });
}

async function launchEmbeddedSignup({ configId, extras, projectId = null, onStep }) {
  let loginCode = null;

  const [sessionInfo] = await Promise.all([
    waitForEmbeddedSignupFinish({
      projectId,
      getCode: () => loginCode,
      onStep,
    }),
    requestFacebookLoginCode({ configId, extras }).then((response) => {
      loginCode = response?.authResponse?.code || null;
      return loginCode;
    }),
  ]);

  return sessionInfo;
}

function ModalShell({ titleId, title, onClose, children }) {
  return (
    <div className="create-channel-backdrop" onClick={onClose} role="presentation">
      <div
        className="create-channel-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="create-channel-modal-header">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="create-channel-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ConnectResultModal({
  success,
  error,
  selectedProjectId,
  onClose,
  onRetry,
}) {
  return (
    <ModalShell
      titleId="connect-whatsapp-title"
      title={success ? "WhatsApp Connected" : "WhatsApp Connection Failed"}
      onClose={onClose}
    >
      <div style={{ padding: "0.25rem 0 0.9rem" }}>
        {success ? (
          <>
            <p style={{ margin: 0 }}>
              WhatsApp connection saved. You can start sending campaigns and receiving live chats.
            </p>
            {selectedProjectId && (
              <div style={{ marginTop: "0.85rem" }}>
                <Link
                  to={businessPath(selectedProjectId, "myprofile")}
                  className="all-projects-create-btn"
                  style={{ textDecoration: "none", justifyContent: "center" }}
                >
                  Go to My Profile
                </Link>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="all-projects-status-hint" style={{ color: "var(--red-700)", margin: 0 }}>
              {error || "Failed to connect WhatsApp."}
            </p>
            {onRetry && (
              <button
                type="button"
                className="create-channel-start-btn"
                style={{ marginTop: "0.85rem" }}
                onClick={onRetry}
              >
                Try again
              </button>
            )}
          </>
        )}
      </div>
    </ModalShell>
  );
}

const ConnectWhatsapp = forwardRef(({
  projectId = null,
  trigger,
  hideTrigger = false,
  autoLaunch: _autoLaunch = false,
  onConnected,
}, ref) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(CONNECT_STEPS.IDLE);
  const [connectedProjectId, setConnectedProjectId] = useState("");
  const [error, setError] = useState("");
  const incomingProjectId = normalizeProjectId(projectId);

  useEffect(() => {
    if (!META_APP_ID) return;
    loadFacebookSdk().catch(() => {});
  }, []);

  const resetModal = () => {
    setStep(CONNECT_STEPS.IDLE);
    setError("");
  };

  const handleStartEmbeddedSignup = async () => {
    setError("");
    setModalOpen(false);
    setStep(CONNECT_STEPS.SIGNUP_POPUP);

    try {
      if (!window.FB && window.__fbSdkPromise) {
        await window.__fbSdkPromise;
      }

      if (!window.FB) {
        throw new Error(
          META_APP_ID
            ? "Facebook SDK is not available yet. Refresh and try again in a moment."
            : "Missing REACT_APP_META_APP_ID (set it in wa_interface/.env.development and .env.production, then restart the frontend).",
        );
      }

      if (!META_CONFIG_ID) {
        throw new Error("Missing REACT_APP_META_CONFIG_ID in environment.");
      }

      const sessionInfo = await launchEmbeddedSignup({
        configId: META_CONFIG_ID,
        extras: {
          setup: {},
          sessionInfoVersion: "3",
          version: "v4",
          featureType: "whatsapp_business_app_onboarding",
        },
        projectId: incomingProjectId,
        onStep: setStep,
      });

      if (!sessionInfo?.waba_id) {
        throw new Error("WhatsApp signup did not return a WABA ID.");
      }

      setConnectedProjectId(sessionInfo.projectId || "");
      setStep(CONNECT_STEPS.SAVED);
      setModalOpen(true);
      onConnected?.(sessionInfo);
    } catch (err) {
      setError(err?.message || "Failed to connect WhatsApp.");
      setStep(CONNECT_STEPS.FAILED);
      setModalOpen(true);
    }
  };

  const openModal = () => {
    resetModal();
    handleStartEmbeddedSignup();
  };

  const closeModal = () => {
    setModalOpen(false);
    resetModal();
  };

  useImperativeHandle(ref, () => ({
    open: openModal,
    launch: handleStartEmbeddedSignup,
  }));

  const showResultModal =
    modalOpen && (step === CONNECT_STEPS.SAVED || step === CONNECT_STEPS.FAILED);

  return (
    <>
      {!hideTrigger && (
        typeof trigger === "function" ? (
          trigger({ onClick: openModal })
        ) : (
          <button
            type="button"
            className="all-projects-create-btn"
            style={{ background: "var(--green-900)" }}
            onClick={openModal}
          >
            <FaWhatsapp aria-hidden />
            Connect WhatsApp
          </button>
        )
      )}

      {showResultModal && (
        <ConnectResultModal
          success={step === CONNECT_STEPS.SAVED}
          error={error}
          selectedProjectId={connectedProjectId}
          onClose={closeModal}
          onRetry={
            step === CONNECT_STEPS.FAILED
              ? () => handleStartEmbeddedSignup()
              : undefined
          }
        />
      )}
    </>
  );
});

ConnectWhatsapp.displayName = "ConnectWhatsapp";

export default ConnectWhatsapp;
